import { Form, Formik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import Modal from '../../../SharedComponents/Modal';
import Cog from '../../../SharedComponents/Svg/Cog';
import toast from 'react-hot-toast';
import {
  editPermissions,
  editRole,
  getAllSystemPermissions,
  getPermissions,
  rolesErrorMessage,
} from './ApiRoles';
import Search from '../../../SharedComponents/Svg/Search';
import Permissions from './Permissions';
import ChevronRight from '../../../SharedComponents/Svg/ChevronRight';
import ChevronLeft from '../../../SharedComponents/Svg/ChevronLeft';
import { useTranslation } from 'react-i18next';

const ModalContainer = (props) => {
  const { id, name, isOpen, setIsOpen } = props;

  const [searchPermissions, setSearchPermissions] = useState('');
  const [currentPermissions, setCurrentPermissions] = useState([]);
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  // Keyed by role: the key used to be the constant 'Permissions', so every
  // role's modal shared one cache entry and could show another role's list.
  const { data, refetch } = useQuery(['Permissions', name], getPermissions(name), {
    staleTime: 7000,
  });

  // Driven off `data` instead of onSuccess, which does not fire when the row is
  // served from cache on a second open.
  useEffect(() => {
    setCurrentPermissions(data?.data ?? []);
  }, [data]);

  // Ticked-but-not-yet-moved items. These have to survive re-renders, so they
  // live in refs: as plain arrays declared in the component body they were
  // recreated on every render, silently discarding selections while the
  // checkboxes (being uncontrolled) still looked ticked.
  const newCurrentPermissions = useRef([]);
  const newAllPermissions = useRef([]);

  // The catalogue comes from the API. `Permissions` is only a fallback for
  // accounts without RolePerm.List - the bundled copy had drifted well behind
  // the backend (102 entries against 157), so whole modules were ungrantable.
  const { data: systemPermissions } = useQuery('SystemPermissions', getAllSystemPermissions, {
    staleTime: 5 * 60 * 1000,
    retry: false,
    onError: () => {},
  });

  // Union with the role's own claims so a permission the catalogue does not
  // list still appears rather than silently vanishing from the picker.
  const allPermissions = useMemo(
    () => [...new Set([...(systemPermissions ?? Permissions), ...currentPermissions])],
    [systemPermissions, currentPermissions]
  );

  /** Track/untrack a tick in the given staging ref. */
  const toggleStaged = (ref) => (e) => {
    const id = e.target.dataset.id;
    if (e.target.checked) {
      if (!ref.current.includes(id)) ref.current.push(id);
    } else {
      ref.current = ref.current.filter((x) => x !== id);
    }
  };

  const handleAllPermissions = toggleStaged(newAllPermissions);
  const handleCurrentPermissions = toggleStaged(newCurrentPermissions);

  /** Clear the ticks once a transfer has been applied, both list and DOM. */
  const clearStaged = () => {
    newAllPermissions.current = [];
    newCurrentPermissions.current = [];
    document
      .querySelectorAll('input[type=checkbox][data-id]')
      .forEach((box) => {
        box.checked = false;
      });
  };

  const handleLeftToRight = () => {
    if (newAllPermissions.current.length === 0) return;
    // Deduplicate: the same permission must never be sent twice.
    setCurrentPermissions([...new Set([...currentPermissions, ...newAllPermissions.current])]);
    clearStaged();
  };

  const handleRightToLeft = () => {
    if (newCurrentPermissions.current.length === 0) return;
    const toRemove = new Set(newCurrentPermissions.current);
    setCurrentPermissions(currentPermissions.filter((x) => !toRemove.has(x)));
    clearStaged();
  };
  const onSearchPermissions = (e) => {
    setSearchPermissions(e.target.value);
  };

  const { mutateAsync, isLoading: isSaving } = useMutation('editPermissions', editPermissions, {
    onSuccess: () => {
      // Re-read this role, so what is shown is what the server actually stored.
      queryClient.invalidateQueries(['Permissions', name]);
    },
  });

  const onSubmit = async () => {
    try {
      // mutateAsync (not mutate) so this actually waits for the request, and the
      // result of the *mutation* decides success - the old code awaited a
      // non-promise and then read `data`, which is the query, not the save.
      const res = await mutateAsync({ roleName: name, claims: currentPermissions });
      if (res?.isDone) {
        clearStaged();
        setIsOpen(false);
      } else {
        toast.error(t('Something went wrong'));
      }
    } catch (error) {
      toast.error(t(rolesErrorMessage(error)) || t('Something went wrong'));
    }
  };
  return (
    <Formik
      initialValues={{
        currentPermissions,
        allPermissions,
      }}
      onSubmit={onSubmit}
    >
      {({ values }) => {
        return (
          <Form>
            <Modal
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              title={`${t('Edit Role')} ${name} ${'Permissions'}`}
              add={true}
              onSubmit={onSubmit}
              submitType="button"
              isLoading={isSaving}
              className="sm:max-w-3xl"
            >
              <div className="flex justify-between">
                <div className="border rounded w-80">
                  <div className="px-5 py-4 border-b">
                    <label
                      className={`text-sm font-normal text-gray-500 flex items-center space-x-2 ${
                        i18n.language === 'en' ? '' : 'space-x-reverse'
                      }`}
                    >
                      <input className="border-gray-500 text-gray-500 rounded-none" key={222} type="checkbox" />
                      <span>
                        {allPermissions.length} {t('items')}
                      </span>
                    </label>
                  </div>
                  <div className="flex items-center border my-4 mx-4 p-1 px-2 rounded">
                    <input
                      className="w-full focus:outline-none"
                      type="text"
                      placeholder={t('Search here')}
                      onChange={onSearchPermissions}
                    />
                    <Search className="w-4 h-4" />
                  </div>

                  <div className=" flex flex-col overflow-y-scroll max-h-56 space-y-2 pl-5">
                    {allPermissions &&
                      allPermissions
                        .filter((e) => !currentPermissions.includes(e))
                        .filter((e) => e.toLowerCase().includes(searchPermissions.toLowerCase()))
                        .map((allPermission) => (
                          // Plain label: each row used to be wrapped in a
                          // FieldArray named "pgPageLngs" (copied from a page
                          // form), registering one Formik array per permission
                          // for no reason and tripping the missing-key warning.
                          <label
                            key={allPermission}
                            className={`text-sm font-normal text-gray-500 flex items-center space-x-2 ${
                              i18n.language === 'en' ? '' : 'space-x-reverse'
                            }`}
                          >
                            <input
                              className="border-gray-500 text-gray-500 rounded-none"
                              onChange={handleAllPermissions}
                              data-id={allPermission}
                              type="checkbox"
                            />
                            <span>{allPermission}</span>
                          </label>
                        ))}
                  </div>
                  <div className=" border-t text-center text-xs text-gray-400 font-normal py-2">
                    {t('All Permissions')}
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="flex flex-col space-y-3">
                    <button
                      type="button"
                      className="bg-gray-100 hover:bg-gray-200 text-gray-300 rounded p-1"
                      onClick={handleLeftToRight}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      className="bg-gray-100 hover:bg-gray-200 text-gray-300 rounded p-1"
                      onClick={handleRightToLeft}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="border rounded w-80">
                  <div className="px-5 py-4 border-b">
                    <label
                      className={`text-sm font-normal text-gray-500 flex items-center space-x-2 ${
                        i18n.language === 'en' ? '' : 'space-x-reverse'
                      }`}
                    >
                      <input className="border-gray-500 text-gray-500 rounded-none" key={222} type="checkbox" />
                      <span>
                        {currentPermissions.length} {t('items')}
                      </span>
                    </label>
                  </div>
                  <div className="flex items-center border my-4 mx-4 p-1 px-2 rounded">
                    <input
                      className="w-full focus:outline-none"
                      type="text"
                      placeholder={t('Search here')}
                      onChange={onSearchPermissions}
                    />
                    <Search className="w-4 h-4" />
                  </div>

                  <div className=" flex flex-col overflow-y-scroll max-h-56 space-y-2 pl-5">
                    {currentPermissions
                      .filter((e) => e.toLowerCase().includes(searchPermissions.toLowerCase()))
                      .map((currentPermission) => {
                        return (
                          <label
                            key={currentPermission}
                            className={`text-sm font-normal text-gray-500 flex items-center space-x-2 ${
                              i18n.language === 'en' ? '' : 'space-x-reverse'
                            }`}
                          >
                            <input
                              className="border-gray-500 text-gray-500 rounded-none"
                              value={currentPermission}
                              onChange={handleCurrentPermissions}
                              data-id={currentPermission}
                              type="checkbox"
                            />
                            <span>{t(currentPermission)}</span>
                          </label>
                        );
                      })}
                  </div>
                  <div className=" border-t text-center text-xs text-gray-400 font-normal py-2">
                    {t('Current Permissions')}
                  </div>
                </div>
              </div>
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};

export default function RolePermission(props) {
  const [isOpen, setIsOpen] = useState(false);
  const { id, name } = props;

  return (
    <>
      <button
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200  p-1 rounded transition duration-300 ease-in-out"
      >
        <Cog className="w-5 h-5" />
      </button>
      {isOpen && <ModalContainer id={id} isOpen={isOpen} setIsOpen={setIsOpen} name={name} />}
    </>
  );
}
